import type { NoteType } from '@compass/client/types/note';
import { noteService } from '@services/api';
import { useStore } from '@store/index';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { toast } from 'sonner';

interface UseNotesOptions {
  type?: NoteType;
  limit?: number;
  enabled?: boolean;
}

// Hook to list notes for a case record
export function useNotes(caseRecordId: string | null, options: UseNotesOptions = {}) {
  const { type, limit, enabled = true } = options;
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  const isEnabled = useMemo(() => {
    return !!caseRecordId && !!selectedOrganizationId && !!selectedProgramId && enabled;
  }, [caseRecordId, selectedOrganizationId, selectedProgramId, enabled]);

  return useQuery({
    queryKey: ['notes', caseRecordId, { type, limit }],
    queryFn: () =>
      noteService.listNotes({
        organization_id: selectedOrganizationId!,
        program_id: selectedProgramId!,
        case_record_id: caseRecordId!,
        type,
        limit,
      }),
    enabled: isEnabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

// Hook to create a new note
export function useCreateNote(caseRecordId: string | null) {
  const queryClient = useQueryClient();
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  return useMutation({
    mutationFn: (content: string) => {
      if (!caseRecordId || !selectedOrganizationId || !selectedProgramId) {
        throw new Error('Missing context for create note');
      }
      return noteService.createNote({
        organization_id: selectedOrganizationId,
        program_id: selectedProgramId,
        case_record_id: caseRecordId,
        content,
      });
    },
    onSuccess: () => {
      // Invalidate notes query to refetch the list
      queryClient.invalidateQueries({
        queryKey: ['notes', caseRecordId],
      });
      toast.success('Note added successfully');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Failed to add note';
      toast.error(errorMessage);
    },
  });
}
